import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { getSales, createSale, updateSale, deleteSale } from '../../apis/sales';
import { RootState } from '../store';

export interface Sale {
    id: string;
    full_name: string;
    email: string;
    role?: string;
    organization_id?: string | null;
    organization_name?: string | null;
    phone_number?: string;
    bio?: string;
    profile_picture_url?: string;
    total_credits?: number;
    deleted_at?: string | null;
    profile?: any;
}

interface SaleState {
    sales: Sale[];
    total: number;
    page: number;
    size: number;
    pages: number;
    loading: boolean;
    error: string | null;
}

const initialState: SaleState = {
    sales: [],
    total: 0,
    page: 1,
    size: 12,
    pages: 1,
    loading: false,
    error: null,
};

// Thunks
export const fetchSales = createAsyncThunk(
    'sales/fetchSales',
    async (params: Record<string, any> = {}, { rejectWithValue }) => {
        try {
            const response = await getSales(params);
            return response.data;
        } catch (err: any) {
            const pl = err.response?.data;
            const errorText = pl?.detail
                ? (typeof pl.detail === 'string' ? pl.detail : JSON.stringify(pl.detail))
                : (typeof pl === 'string' ? pl : 'Could not fetch sales');
            return rejectWithValue(errorText);
        }
    }
);

export const addSale = createAsyncThunk(
    'sales/addSale',
    async (data: Partial<Sale>, { rejectWithValue }) => {
        try {
            const response = await createSale(data);
            return response.data;
        } catch (err: any) {
            const pl = err.response?.data;
            const errorText = pl?.detail
                ? (typeof pl.detail === 'string' ? pl.detail : JSON.stringify(pl.detail))
                : (typeof pl === 'string' ? pl : 'Could not add sale');
            return rejectWithValue(errorText);
        }
    }
);

export const editSale = createAsyncThunk(
    'sales/editSale',
    async ({ id, data }: { id: string; data: Partial<Sale> }, { rejectWithValue }) => {
        try {
            const response = await updateSale(id, data);
            return response.data;
        } catch (err: any) {
            const pl = err.response?.data;
            const errorText = pl?.detail
                ? (typeof pl.detail === 'string' ? pl.detail : JSON.stringify(pl.detail))
                : (typeof pl === 'string' ? pl : 'Could not edit sale');
            return rejectWithValue(errorText);
        }
    }
);

export const removeSale = createAsyncThunk(
    'sales/removeSale',
    async (id: string, { rejectWithValue }) => {
        try {
            await deleteSale(id);
            return id;
        } catch (err: any) {
            const pl = err.response?.data;
            const errorText = pl?.detail
                ? (typeof pl.detail === 'string' ? pl.detail : JSON.stringify(pl.detail))
                : (typeof pl === 'string' ? pl : 'Could not delete sale');
            return rejectWithValue(errorText);
        }
    }
);

const saleSlice = createSlice({
    name: 'sales',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchSales.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchSales.fulfilled, (state, action: PayloadAction<any>) => {
                state.loading = false;
                state.sales = action.payload.items;
                state.total = action.payload.total;
                state.page = action.payload.page;
                state.size = action.payload.size;
                state.pages = action.payload.pages;
            })
            .addCase(fetchSales.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(addSale.fulfilled, (state, action: PayloadAction<Sale>) => {
                state.sales.push(action.payload);
            })
            .addCase(editSale.fulfilled, (state, action: PayloadAction<Sale>) => {
                const index = state.sales.findIndex((t) => t.id === action.payload.id);
                if (index !== -1) state.sales[index] = action.payload;
            })
            .addCase(removeSale.fulfilled, (state, action: PayloadAction<string>) => {
                state.sales = state.sales.filter((t) => t.id !== action.payload);
            });
    },
});

export const selectAllSales = (state: RootState) => state.sales.sales;
export const selectSaleById = (state: RootState, id: string) =>
    state.sales.sales.find((t) => t.id === id);

export default saleSlice.reducer;
